<template>
    <DialogContainer :isOpen="isOpen" :onClose="closeModal" :maxWidth="'xl'">
        <template v-slot:title>
            <div class="flex items-center justify-center pb-2">
                Export to NGP VAN
            </div>
        </template>

        <template v-slot:buttons>
            <button type="button" class="button-submit sm:ml-3" @click="runExport">
                Export
            </button>
            <button type="button" @click="closeModal" class="button-cancel">
                Cancel
            </button>
        </template>

        <template v-slot:content>
            <div class="py-4">
                <fieldset>
                    <legend class="sr-only">Score Selection</legend>

                    <div class="relative flex items-start py-4">
                        <div class="ml-3 flex items-center h-5">
                            <input id="cb-bestDeliverability" type="radio"
                                class="focus:ring-red-500 h-4 w-4 text-red-600 border-red-300" v-model="selectedScore"
                                value="bestDeliverability" :checked="selectedScore === 'bestDeliverability'" />
                        </div>
                        <div class="min-w-0 flex-1 text-sm px-3">
                            <label for="cb-bestDeliverability" class="font-bold text-gray-600">Best
                                Deliverability</label>
                            <p class="text-gray-400 text-sm mt-1">
                                Export
                                <span class="font-bold text-black" v-if="bestDeliveryCount > 1">{{ bestDeliveryCount }}
                                    emails</span>
                                <span class="font-bold text-black" v-else-if="bestDeliveryCount === 1">{{
                                    bestDeliveryCount }} email</span>
                                that Alfred determined to have the highest deliverability and lowest threat.
                            </p>
                        </div>
                    </div>

                    <div class="relative flex items-start py-4">
                        <div class="ml-3 flex items-center h-5">
                            <input id="cb-maximumReach" type="radio"
                                class="focus:ring-red-500 h-4 w-4 text-red-600 border-red-300" v-model="selectedScore"
                                value="maximumReach" :checked="selectedScore === 'maximumReach'" />
                        </div>
                        <div class="min-w-0 flex-1 text-sm px-3">
                            <label for="cb-maximumReach" class="font-bold text-gray-600">
                                Maximum Reach
                            </label>
                            <p class="text-gray-400 text-sm mt-1">
                                Export
                                <span class="font-bold text-black" v-if="maximumReachCount > 1">{{ maximumReachCount }}
                                    emails</span>
                                <span class="font-bold text-black" v-else-if="maximumReachCount === 1">{{
                                    maximumReachCount }} email</span>
                                that Alfred determined to be likely deliverable with low-to-moderate threat levels.
                            </p>
                        </div>
                    </div>
                </fieldset>
            </div>
        </template>
    </DialogContainer>
</template>

<script>
import DialogContainer from "@/components/common/DialogContainer";
import modalMixin from "@/mixins/modalMixin";
import eventBus from "@/utils/eventBus";
import useConfirmationModal from "@/api/confirmationModal";

export default {
    mixins: [modalMixin],
    components: { DialogContainer },

    data() {
        return {
            modalEventName: "show-export-ngpvan",
            isOpen: false,
            showPopup: false,
            job: {},
            selectedScore: "bestDeliverability",
            bestDeliveryCount: 0,
            maximumReachCount: 0,
            job_emails_count: 0
        };
    },

    methods: {
        openModal({ job }) {
            this.job = job;
            this.isOpen = true;
            this.selectedScore = "bestDeliverability";
            this.calculateEmailCounts();
        },

        calculateEmailCounts() {
            this.job_emails_count = this.job.basicData?.emailsCount || 0;

            if (
                this.job.result &&
                this.job.result.verification_job &&
                this.job.result.verification_job.scores &&
                this.job.result.verification_job.scores.length > 0
            ) {
                this.bestDeliveryCount = this.job.result.verification_job.scores
                    .filter((x) => x.score >= 7 && x.score <= 10)
                    .reduce((a, b) => a + b.email_count, 0);

                this.maximumReachCount = this.job.result.verification_job.scores
                    .filter((x) => x.score >= 6 && x.score <= 10)
                    .reduce((a, b) => a + b.email_count, 0);
            } else {
                this.bestDeliveryCount = 0;
                this.maximumReachCount = 0;
            }
        },

        closeModal() {
            this.isOpen = false;
            this.showPopup = false;
        },

        openPopup() {
            this.showPopup = true;
            this.isOpen = false;
        },

        async runExport() {
            if (!this.selectedScore) {
                await eventBus.trigger("notify", {
                    notification: {
                        group: "top-right",
                        text: "Please select at least one option before exporting.",
                        type: "warning",
                    },
                });
                return;
            }

            this.isOpen = false;
            const modal = useConfirmationModal();

            await modal.openModal({
                title: "Are you sure you want to export the list",
                onSubmit: () => this.runExportJob(),
                onClose: this.onClose,
            });
        },
        async runExportJob() {
            let scores = [];
            if (this.selectedScore === "bestDeliverability") {
                scores = [7, 8, 9, 10];
            } else if (this.selectedScore === "maximumReach") {
                scores = [6, 7, 8, 9, 10];
            }

            try {
                await this.$alfredService.ngpVanExport({
                    job_id: this.job.job_id,
                    scores,
                });
                await eventBus.trigger("notify", {
                    notification: {
                        group: "top-right",
                        text: "The list export initiated successfully!",
                        type: "success",
                    },
                });
                this.closeModal();
            } catch (e) {
                await eventBus.trigger("notify", {
                    notification: {
                        group: "top-right",
                        text: e.response.data.detail,
                        type: "error",
                    },
                });
            }
        },
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
